import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CandidateDetails from './CandidateDetails';
import axios from 'axios';
import {
    Briefcase, MapPin, Clock, Zap, Eye, Calendar,
    Star, Check, X, Plus, ArrowRight, Edit2,
} from 'lucide-react';

/* ─── Helpers ─── */
function timeAgo(dt) {
    if (!dt) return '';
    const diff = Date.now() - new Date(dt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatDateTime(dt) {
    if (!dt) return { date: '—', time: '—' };
    const d = new Date(dt);
    const date = d.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });
    const time = d.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' });
    return { date, time };
}

function jobTags(job) {
    if (!job.tags) return [];
    return job.tags.split(',').map(t => t.trim()).filter(Boolean);
}

function initials(name = '') {
    return name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const AVATAR_PALETTE = ['#d97706', '#0f6f57', '#3b82f6', '#8b5cf6', '#f97316', '#0891b2'];
function avatarColor(str = '') {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function applicantName(app) {
    const u = app.user;
    if (!u) return 'Unknown';
    if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
    return u.username || 'Unknown';
}

/* ─── Status badge ─── */
function StatusBadge({ status }) {
    const MAP = {
        ACTIVE:    { label: 'Active',     cls: 'ml-badge-active' },
        SCHEDULED: { label: 'Scheduled',  cls: 'ml-badge-scheduled' },
        COMPLETED: { label: 'Completed',  cls: 'ml-badge-completed' },
        DRAFT:     { label: 'Draft',      cls: 'ml-badge-draft' },
        CANCELLED: { label: 'Cancelled',  cls: 'ml-badge-cancelled' },
    };
    const m = MAP[status] || { label: status, cls: 'ml-badge-draft' };
    return (
        <span className={`ml-badge ${m.cls}`}>
            <span className="ml-badge-dot" />
            {m.label}
        </span>
    );
}

/* ─── Candidate chip (in the inline strip) ─── */
function CandidateChip({ app, statuses, onSetStatus, onClick }) {
    const name = applicantName(app);
    const ini  = initials(name);
    const bg   = avatarColor(name);
    const st   = statuses[app.id] || app.status; // local override or server status
    const isShortlisted = st === 'ACCEPTED' || st === 'shortlisted';
    const isRejected    = st === 'REJECTED'  || st === 'rejected';

    return (
        <div className={`ml-cand${isShortlisted ? ' is-short' : ''}${isRejected ? ' is-rejected' : ''}`}>
            <button className="ml-cand-main" onClick={onClick}>
                <span className="ml-cand-avatar" style={{ background: bg, color: '#fff' }}>{ini}</span>
                <span className="ml-cand-info">
                    <span className="ml-cand-name">{name}</span>
                    <span className="ml-cand-meta">
                        ★ 4.9 · {timeAgo(app.applicationDate)}
                    </span>
                </span>
            </button>
            <div className="ml-cand-actions">
                <button
                    className={`ml-cand-btn ml-cand-yes${isShortlisted ? ' is-on' : ''}`}
                    title="Accept"
                    onClick={() => onSetStatus(app.id, 'ACCEPTED')}
                >
                    <Check size={12} />
                </button>
                <button
                    className={`ml-cand-btn ml-cand-no${isRejected ? ' is-on' : ''}`}
                    title="Reject"
                    onClick={() => onSetStatus(app.id, 'REJECTED')}
                >
                    <X size={12} />
                </button>
            </div>
        </div>
    );
}

/* ─── Listing card ─── */
function ListingCard({ job, applications, statuses, onSetStatus, onOpen, onOpenCandidate, onMarkDone, onCancel }) {
    const isActive    = job.jobStatus === 'ACTIVE';
    const isScheduled = job.jobStatus === 'SCHEDULED';
    const isDone      = job.jobStatus === 'COMPLETED';
    const isDraft     = job.jobStatus === 'DRAFT';
    const { date, time } = formatDateTime(job.dateTime);
    const tags = jobTags(job);
    const isUrgent = job.dateTime && new Date(job.dateTime).toDateString() === new Date().toDateString();

    const acceptedApps = applications.filter(a => a.status === 'ACCEPTED');
    const pendingApps  = applications.filter(a => a.status === 'PENDING');
    const navigate = useNavigate();

    // Poster → accepted worker: open (or create) the job-scoped conversation
    // with the first confirmed worker, then jump to the inbox with it selected.
    const startChatWithWorker = async () => {
        const workerId = acceptedApps[0]?.user?.id;
        if (!workerId) { alert('No accepted worker to message yet.'); return; }
        try {
            const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            const r = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/conversations/start`,
                { jobId: job.id, workerId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            navigate(`/messages?c=${r.data.id}`);
        } catch {
            alert('Could not open chat.');
        }
    };
    const recentCount  = pendingApps.filter(a => {
        const diff = Date.now() - new Date(a.applicationDate).getTime();
        return diff < 60 * 60 * 1000; // last hour
    }).length;

    return (
        <article className={`ml-card${isDraft ? ' is-draft' : ''}${isUrgent ? ' is-urgent' : ''}`}>
            {isUrgent && <span className="ml-card-stripe" aria-hidden />}

            {/* Head */}
            <div className="ml-card-head">
                <div className="ml-card-head-l">
                    <div className="ml-card-meta">
                        <StatusBadge status={job.jobStatus} />
                        {isUrgent && (
                            <span className="ml-meta-item" style={{ color: '#e07b54' }}>
                                <Zap size={11} /> Today
                            </span>
                        )}
                        {!isDraft && job.viewCount > 0 && (
                            <span className="ml-meta-item"><Eye size={11} /> {job.viewCount} views</span>
                        )}
                        <span className="ml-meta-item">Posted {timeAgo(job.createdAt || job.dateTime)}</span>
                    </div>
                    <h2 className="ml-card-title">{job.title}</h2>
                    {tags.length > 0 && (
                        <div className="ml-card-tags">
                            {tags.map(t => <span key={t} className="ml-tag">#{t}</span>)}
                        </div>
                    )}
                </div>

                <div className="ml-card-pay">
                    <div className="ml-card-pay-amt">€{job.payment}</div>
                    <div className="ml-card-pay-meta">{job.duration || 'TBD'}</div>
                </div>
            </div>

            {/* Facts */}
            <div className="ml-card-facts">
                <div className="ml-fact">
                    <Calendar size={14} />
                    <div>
                        <div className="ml-fact-l">{date}</div>
                        <div className="ml-fact-sub">{time}</div>
                    </div>
                </div>
                <div className="ml-fact">
                    <MapPin size={14} />
                    <div>
                        <div className="ml-fact-l">{job.address || '—'}</div>
                        <div className="ml-fact-sub">
                            {isDraft ? '—' : 'Exact address shared on accept'}
                        </div>
                    </div>
                </div>
                {isActive && (
                    <div className="ml-fact">
                        <Clock size={14} />
                        <div>
                            <div className="ml-fact-l">
                                {applications.length} of {job.applicantLimit || '—'}
                            </div>
                            <div className="ml-fact-sub">Applicant cap</div>
                        </div>
                    </div>
                )}
                {isScheduled && acceptedApps.length > 0 && (
                    <div className="ml-fact">
                        <Star size={14} />
                        <div>
                            <div className="ml-fact-l">
                                {acceptedApps.length === 1
                                    ? applicantName(acceptedApps[0])
                                    : `${acceptedApps.length} workers confirmed`}
                            </div>
                            <div className="ml-fact-sub">Accepted worker</div>
                        </div>
                    </div>
                )}
                {isDone && (
                    <div className="ml-fact">
                        <Check size={14} />
                        <div>
                            <div className="ml-fact-l">Completed</div>
                            <div className="ml-fact-sub">
                                {acceptedApps.length
                                    ? `${acceptedApps.length} worker${acceptedApps.length > 1 ? 's' : ''}`
                                    : 'Job done'}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Inline candidate strip (active jobs only) */}
            {isActive && pendingApps.length > 0 && (
                <div className="ml-cands">
                    <div className="ml-cands-head">
                        <span className="ml-cands-title">{pendingApps.length} candidate{pendingApps.length !== 1 ? 's' : ''}</span>
                        {recentCount > 0 && (
                            <span className="ml-cands-fresh">
                                <span className="ml-dot ml-dot-fresh" />
                                {recentCount} new in last hour
                            </span>
                        )}
                    </div>
                    <div className="ml-cands-row">
                        {pendingApps.slice(0, 4).map(app => (
                            <CandidateChip
                                key={app.id}
                                app={app}
                                statuses={statuses}
                                onSetStatus={onSetStatus}
                                onClick={() => onOpenCandidate(app)}
                            />
                        ))}
                        {pendingApps.length > 4 && (
                            <button className="ml-cands-more" onClick={onOpen}>
                                +{pendingApps.length - 4} more
                            </button>
                        )}
                    </div>
                </div>
            )}

            {isActive && pendingApps.length === 0 && applications.length === 0 && (
                <div className="ml-cands ml-cands-empty">
                    <span className="ml-dot ml-dot-pulse" />
                    <span>Listening for applicants — average wait 11 min.</span>
                </div>
            )}

            {/* Footer actions */}
            <div className="ml-card-foot">
                {isActive && (
                    <button className="ix-btn ix-btn-primary ix-btn-sm" onClick={onOpen}>
                        Review candidates <ArrowRight size={12} />
                    </button>
                )}
                {isScheduled && (
                    <>
                        <button className="ix-btn ix-btn-primary ix-btn-sm" onClick={onMarkDone}>
                            <Check size={12} /> Mark job done
                        </button>
                        <button className="ix-btn ix-btn-outline ix-btn-sm" onClick={startChatWithWorker}>Message worker</button>
                    </>
                )}
                {isDone && (
                    <button className="ix-btn ix-btn-outline ix-btn-sm">View receipt</button>
                )}
                {isDraft && (
                    <Link to="/create-job" className="ix-btn ix-btn-primary ix-btn-sm">
                        <Edit2 size={12} /> Continue editing
                    </Link>
                )}
                {!isDraft && !isDone && (
                    <button className="ix-btn ix-btn-outline ix-btn-sm">Edit listing</button>
                )}
                {(isActive || isScheduled) && (
                    <button
                        className="ix-btn ix-btn-ghost ix-btn-sm ml-btn-danger"
                        onClick={onCancel}
                    >
                        Cancel job
                    </button>
                )}
                {isDraft && (
                    <button className="ix-btn ix-btn-ghost ix-btn-sm ml-btn-danger">Discard</button>
                )}
                {isDone && (
                    <button className="ix-btn ix-btn-ghost ix-btn-sm">Repost</button>
                )}
            </div>
        </article>
    );
}

/* ─── Candidate panel (slide-in) ─── */
function CandidatePanel({ job, applications, selected, onSelect, onClose, statuses, onSetStatus, onViewProfile }) {
    const name = selected ? applicantName(selected) : '';
    const ini  = initials(name);
    const bg   = avatarColor(name);
    const st   = selected ? (statuses[selected.id] || selected.status) : null;

    return (
        <>
            <div className="ml-overlay" onClick={onClose} />
            <aside className="ml-panel">
                <header className="ml-panel-head">
                    <div>
                        <div className="ml-panel-eyebrow">Reviewing for</div>
                        <h3 className="ml-panel-title">{job.title}</h3>
                    </div>
                    <button
                        className="ix-btn ix-btn-ghost"
                        style={{ padding: '6px' }}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </header>

                <div className="ml-panel-body">
                    {/* Left: list */}
                    <div className="ml-panel-list">
                        {applications.map(app => {
                            const n  = applicantName(app);
                            const iv = initials(n);
                            const bv = avatarColor(n);
                            const s  = statuses[app.id] || app.status;
                            return (
                                <button
                                    key={app.id}
                                    className={
                                        `ml-panel-item` +
                                        (selected?.id === app.id ? ' is-active' : '') +
                                        (s === 'ACCEPTED' || s === 'shortlisted' ? ' is-short' : '') +
                                        (s === 'REJECTED' || s === 'rejected' ? ' is-rejected' : '')
                                    }
                                    onClick={() => onSelect(app)}
                                >
                                    <span className="ml-cand-avatar" style={{ background: bv, color: '#fff' }}>{iv}</span>
                                    <span className="ml-panel-item-info">
                                        <span className="ml-cand-name">{n}</span>
                                        <span className="ml-cand-meta">★ 4.9 · {timeAgo(app.applicationDate)}</span>
                                    </span>
                                    {(s === 'ACCEPTED' || s === 'shortlisted') && (
                                        <Check size={14} className="ml-panel-item-flag" />
                                    )}
                                    {(s === 'REJECTED' || s === 'rejected') && (
                                        <X size={14} className="ml-panel-item-flag" style={{ color: 'var(--ink-400)' }} />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Right: detail */}
                    {selected && (
                        <div className="ml-panel-detail">
                            <div className="ml-panel-detail-head">
                                <span
                                    className="ml-cand-avatar ml-cand-avatar-lg"
                                    style={{ background: bg, color: '#fff' }}
                                >
                                    {ini}
                                </span>
                                <div>
                                    <h4 className="ml-panel-detail-name">{name}</h4>
                                    <div className="ml-panel-detail-meta">
                                        <span><Star size={13} color="var(--amber)" fill="var(--amber)" strokeWidth={0} /> 4.9 rating</span>
                                        <span>·</span>
                                        <span>Applied {timeAgo(selected.applicationDate)}</span>
                                        <span>·</span>
                                        <span>Status: {selected.status}</span>
                                    </div>
                                </div>
                            </div>

                            {selected.user?.location && (
                                <section className="ml-panel-section">
                                    <h5 className="ml-panel-section-title">Location</h5>
                                    <p className="ix-body">{selected.user.location}</p>
                                </section>
                            )}

                            {selected.user?.bio && (
                                <section className="ml-panel-section">
                                    <h5 className="ml-panel-section-title">About</h5>
                                    <p className="ix-body">{selected.user.bio}</p>
                                </section>
                            )}

                            <div className="ml-panel-actions">
                                {(st === 'PENDING' || !st) && (
                                    <>
                                        <button
                                            className="ix-btn ix-btn-primary ix-btn-lg"
                                            onClick={() => onSetStatus(selected.id, 'ACCEPTED')}
                                        >
                                            <Check size={14} /> Accept & schedule
                                        </button>
                                        <button
                                            className="ix-btn ix-btn-ghost ml-btn-danger"
                                            onClick={() => onSetStatus(selected.id, 'REJECTED')}
                                        >
                                            Pass
                                        </button>
                                    </>
                                )}
                                {(st === 'ACCEPTED' || st === 'shortlisted') && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--teal-800)', fontSize: '14px', fontWeight: 500 }}>
                                        <Check size={15} /> Accepted — job moves to Scheduled
                                    </span>
                                )}
                                {(st === 'REJECTED' || st === 'rejected') && (
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>Passed on this applicant.</span>
                                )}
                                <button
                                    className="ix-btn ix-btn-outline"
                                    onClick={() => onViewProfile(selected)}
                                >
                                    View full profile
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}

/* ─── Mark Done modal ─── */
function MarkDoneModal({ job, acceptedApps, onClose, onConfirm }) {
    const [step, setStep] = useState('confirm');
    const [confirmedIds, setConfirmedIds] = useState(new Set(acceptedApps.map(a => a.id)));
    const [ratings, setRatings] = useState({});

    const toggle = id => {
        const s = new Set(confirmedIds);
        s.has(id) ? s.delete(id) : s.add(id);
        setConfirmedIds(s);
    };

    const confirmedApps = acceptedApps.filter(a => confirmedIds.has(a.id));
    const noShows       = acceptedApps.filter(a => !confirmedIds.has(a.id));
    const payout = confirmedApps.length * job.payment;

    const StarRating = ({ appId }) => {
        const [hover, setHover] = useState(0);
        const val = ratings[appId] || 0;
        return (
            <div className="ml-stars" onMouseLeave={() => setHover(0)}>
                {[1,2,3,4,5].map(i => (
                    <button
                        key={i}
                        type="button"
                        className={`ml-star${i <= (hover || val) ? ' is-on' : ''}`}
                        onMouseEnter={() => setHover(i)}
                        onClick={() => setRatings(r => ({ ...r, [appId]: i }))}
                    >
                        <Star size={20} />
                    </button>
                ))}
                <span className="ml-stars-label">
                    {val ? `${val} of 5` : 'Tap to rate'}
                </span>
            </div>
        );
    };

    return (
        <>
            <div className="ml-overlay" onClick={onClose} />
            <div className="ml-modal" role="dialog">
                <header className="ml-modal-head">
                    <div>
                        <div className="ml-modal-eyebrow">
                            {step === 'confirm' && 'Step 1 of 2 · Confirm attendance'}
                            {step === 'review'  && 'Step 2 of 2 · Rate workers'}
                            {step === 'done'    && 'All set'}
                        </div>
                        <h3 className="ml-modal-title">{job.title}</h3>
                    </div>
                    <button className="ix-btn ix-btn-ghost" style={{ padding: '6px' }} onClick={onClose}><X size={18} /></button>
                </header>

                {step === 'confirm' && (
                    <div className="ml-modal-body">
                        <p className="lede ml-modal-lede">
                            Tick the workers who actually showed up. Pay is only released for those you confirm.
                        </p>
                        <ul className="ml-md-list">
                            {acceptedApps.map(app => {
                                const n   = applicantName(app);
                                const on  = confirmedIds.has(app.id);
                                const ini = initials(n);
                                const bg  = avatarColor(n);
                                return (
                                    <li key={app.id} className={`ml-md-row${on ? ' is-on' : ''}`}>
                                        <label className="ml-md-row-main">
                                            <input type="checkbox" checked={on} onChange={() => toggle(app.id)} />
                                            <span className="ml-cand-avatar" style={{ background: bg, color: '#fff' }}>{ini}</span>
                                            <span className="ml-md-row-info">
                                                <span className="ml-cand-name">{n}</span>
                                                <span className="ml-cand-meta">★ 4.9</span>
                                            </span>
                                        </label>
                                        <span className={`ml-md-status mono${on ? ' is-show' : ' is-noshow'}`}>
                                            {on ? 'Showed up' : 'No show'}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="ml-md-summary">
                            <div className="ml-md-summary-label">Payout to release</div>
                            <div className="ml-md-summary-value">€{payout}</div>
                            <div className="ml-md-summary-sub">
                                €{job.payment} × {confirmedApps.length} worker{confirmedApps.length !== 1 ? 's' : ''}
                            </div>
                            {noShows.length > 0 && (
                                <div className="ml-md-noshow">
                                    <Zap size={13} />
                                    {noShows.length} no-show{noShows.length !== 1 ? 's' : ''} won't be paid.
                                </div>
                            )}
                        </div>
                        <div className="ml-modal-foot">
                            <button className="ix-btn ix-btn-ghost" onClick={onClose}>Cancel</button>
                            <button
                                className="ix-btn ix-btn-primary"
                                disabled={confirmedApps.length === 0}
                                onClick={() => setStep('review')}
                            >
                                Confirm & continue
                            </button>
                        </div>
                    </div>
                )}

                {step === 'review' && (
                    <div className="ml-modal-body">
                        <p className="lede ml-modal-lede">Leave a quick rating for each worker — it helps the next person hiring.</p>
                        <div className="ml-md-reviews">
                            {confirmedApps.map(app => {
                                const n  = applicantName(app);
                                const bg = avatarColor(n);
                                return (
                                    <div key={app.id} className="ml-md-review">
                                        <div className="ml-md-review-head">
                                            <span className="ml-cand-avatar" style={{ background: bg, color: '#fff' }}>
                                                {initials(n)}
                                            </span>
                                            <span className="ml-md-review-name">{n}</span>
                                        </div>
                                        <StarRating appId={app.id} />
                                        <textarea
                                            className="ml-md-textarea"
                                            placeholder="A line about how it went (optional)"
                                            rows={2}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="ml-modal-foot">
                            <button className="ix-btn ix-btn-ghost" onClick={() => setStep('confirm')}>Back</button>
                            <button
                                className="ix-btn ix-btn-primary"
                                onClick={() => { onConfirm({ confirmedApps, ratings }); setStep('done'); }}
                            >
                                <Check size={14} /> Submit & release €{payout}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'done' && (
                    <div className="ml-modal-body ml-modal-success">
                        <div className="ml-modal-success-mark"><Check size={32} /></div>
                        <h3 className="ml-modal-success-title">Job marked done.</h3>
                        <p className="lede">
                            €{payout} is on its way to {confirmedApps.length} worker{confirmedApps.length !== 1 ? 's' : ''}.
                        </p>
                        <button className="ix-btn ix-btn-primary ix-btn-lg" onClick={onClose}>
                            Back to listings
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}

/* ─── Cancel modal ─── */
const CANCEL_REASONS = [
    'Job no longer needed',
    'Plans changed',
    'Hired someone offline',
    'Posted by mistake',
    'Other',
];

function CancelModal({ job, onClose, onConfirm }) {
    const [reason, setReason] = useState('');
    return (
        <>
            <div className="ml-overlay" onClick={onClose} />
            <div className="ml-modal ml-modal-sm" role="dialog">
                <header className="ml-modal-head">
                    <div>
                        <div className="ml-modal-eyebrow ml-eyebrow-danger">Cancel listing</div>
                        <h3 className="ml-modal-title">{job.title}</h3>
                    </div>
                    <button className="ix-btn ix-btn-ghost" style={{ padding: '6px' }} onClick={onClose}><X size={18} /></button>
                </header>
                <div className="ml-modal-body">
                    <p className="lede ml-modal-lede">
                        This removes the listing and notifies anyone who applied. <strong>This can't be undone.</strong>
                    </p>
                    <div className="ml-cancel-reasons">
                        <div className="ml-md-summary-label">Reason (optional)</div>
                        {CANCEL_REASONS.map(r => (
                            <label key={r} className={`ml-cancel-reason${reason === r ? ' is-on' : ''}`}>
                                <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
                                {r}
                            </label>
                        ))}
                    </div>
                    <div className="ml-modal-foot">
                        <button className="ix-btn ix-btn-ghost" onClick={onClose}>Keep listing</button>
                        <button className="ml-btn-danger-solid" onClick={() => onConfirm(reason)}>
                            Cancel job
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ─── Tabs config ─── */
const TABS = [
    { id: 'ACTIVE',    label: 'Active' },
    { id: 'SCHEDULED', label: 'Scheduled' },
    { id: 'COMPLETED', label: 'Completed' },
    { id: 'DRAFT',     label: 'Drafts' },
];

/* ─── Main component ─── */
const MyListings = () => {
    const [jobs, setJobs]           = useState([]);
    const [appsByJob, setAppsByJob] = useState({}); // { jobId: [applications] }
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);
    const [tab, setTab]             = useState('ACTIVE');
    const [panelJobId, setPanelJobId]       = useState(null);
    const [panelSelected, setPanelSelected] = useState(null);
    const [markDoneJobId, setMarkDoneJobId] = useState(null);
    const [cancelJobId, setCancelJobId]     = useState(null);
    const [statuses, setStatuses]   = useState({}); // { appId: 'ACCEPTED'|'REJECTED' }
    const [toast, setToast]         = useState('');
    const [profileApp, setProfileApp] = useState(null); // application to show in candidate modal
    const navigate = useNavigate();

    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const authHeaders = { Authorization: `Bearer ${token}` };

    const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

    /* Fetch jobs + their applications */
    useEffect(() => {
        if (!token) { setError('You are not logged in.'); setLoading(false); return; }

        const load = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/created`, { headers: authHeaders });
                const jobList = res.data;
                setJobs(jobList);

                const appsMap = {};
                await Promise.all(jobList.map(async job => {
                    try {
                        const ar = await axios.get(
                            `${process.env.REACT_APP_API_BASE_URL}/api/jobs/${job.id}/applied`,
                            { headers: authHeaders }
                        );
                        appsMap[job.id] = ar.data;
                    } catch { appsMap[job.id] = []; }
                }));
                setAppsByJob(appsMap);
            } catch (e) {
                setError('Failed to load listings.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    /* Accept / reject an application */
    const handleSetStatus = useCallback(async (appId, newStatus) => {
        setStatuses(s => ({ ...s, [appId]: newStatus }));
        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/applications/${appId}/status`,
                null,
                { params: { newStatus }, headers: authHeaders }
            );
            if (newStatus === 'ACCEPTED') showToast('Application accepted — job moved to Scheduled.');
        } catch {
            setStatuses(s => ({ ...s, [appId]: undefined }));
            showToast('Failed to update status. Please try again.');
        }
    }, []);

    /* Update job status */
    const updateJobStatus = async (jobId, newStatus) => {
        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}/status`,
                null,
                { params: { newStatus }, headers: authHeaders }
            );
            setJobs(js => js.map(j => j.id === jobId ? { ...j, jobStatus: newStatus } : j));
        } catch {
            showToast('Failed to update job. Please try again.');
        }
    };

    /* Mark done */
    const handleMarkDone = async ({ confirmedApps }) => {
        await updateJobStatus(markDoneJobId, 'COMPLETED');
        setMarkDoneJobId(null);
        showToast('Job marked complete. Payment released.');
    };

    /* Cancel job */
    const handleCancel = async (reason) => {
        await updateJobStatus(cancelJobId, 'CANCELLED');
        setCancelJobId(null);
        showToast('Listing cancelled. Applicants have been notified.');
    };

    /* KPI calculations */
    const counts = TABS.reduce((acc, t) => {
        acc[t.id] = jobs.filter(j => j.jobStatus === t.id).length;
        return acc;
    }, {});
    const totalCandidates = Object.values(appsByJob).flat().filter(a => a.status === 'PENDING').length;
    const monthlyPayout = jobs
        .filter(j => j.jobStatus === 'COMPLETED' || j.jobStatus === 'SCHEDULED')
        .reduce((s, j) => s + (j.payment || 0), 0);

    const filteredJobs = jobs.filter(j => j.jobStatus === tab);
    const panelJob     = jobs.find(j => j.id === panelJobId);
    const markDoneJob  = jobs.find(j => j.id === markDoneJobId);
    const cancelJob    = jobs.find(j => j.id === cancelJobId);

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: 'var(--ink-500)' }}>
            Loading your listings…
        </div>
    );
    if (error) return (
        <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--ink-500)' }}>
            {error}
        </div>
    );

    const handleProfileAccept = async appId => {
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        const res = await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/applications/${appId}/status`,
            null,
            { params: { newStatus: 'ACCEPTED' }, headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.status === 200) {
            setStatuses(prev => ({ ...prev, [appId]: 'ACCEPTED' }));
            setProfileApp(prev => prev ? { ...prev, status: 'ACCEPTED' } : null);
        }
    };

    const handleProfileReject = async appId => {
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        const res = await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/applications/${appId}/status`,
            null,
            { params: { newStatus: 'REJECTED' }, headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.status === 200) {
            setStatuses(prev => ({ ...prev, [appId]: 'REJECTED' }));
            setProfileApp(prev => prev ? { ...prev, status: 'REJECTED' } : null);
        }
    };

    return (
        <>
        {profileApp && (
            <CandidateDetails
                application={profileApp}
                job={profileApp.job}
                onClose={() => setProfileApp(null)}
                onAccept={handleProfileAccept}
                onReject={handleProfileReject}
            />
        )}
        <div className="ml-page">
            <main className="ml-main">
                <div className="ix-container">

                    {/* Page header */}
                    <header className="ml-header">
                        <div>
                            <span className="eyebrow">Your jobs</span>
                            <h1 className="ml-title">
                                My <span className="serif">listings.</span>
                            </h1>
                            <div className="ml-sub">
                                Triage candidates, track scheduled jobs, and keep an eye on your drafts.
                            </div>
                        </div>
                        <Link to="/create-job" className="ix-btn ix-btn-primary">
                            <Plus size={14} /> Post a new job
                        </Link>
                    </header>

                    {/* KPI strip */}
                    <div className="ml-kpis">
                        <div className="ml-kpi">
                            <div className="ml-kpi-label">Open jobs</div>
                            <div className="ml-kpi-value">{counts.ACTIVE || 0}</div>
                            <div className="ml-kpi-sub">Accepting applicants</div>
                        </div>
                        <div className="ml-kpi ml-kpi-teal">
                            <div className="ml-kpi-label">Total candidates</div>
                            <div className="ml-kpi-value">{totalCandidates}</div>
                            <div className="ml-kpi-sub">Awaiting your review</div>
                        </div>
                        <div className="ml-kpi">
                            <div className="ml-kpi-label">Scheduled</div>
                            <div className="ml-kpi-value">{counts.SCHEDULED || 0}</div>
                            <div className="ml-kpi-sub">Confirmed start times</div>
                        </div>
                        <div className="ml-kpi">
                            <div className="ml-kpi-label">Payout this month</div>
                            <div className="ml-kpi-value">€{monthlyPayout.toFixed(0)}</div>
                            <div className="ml-kpi-sub">Across completed jobs</div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <nav className="ml-tabs" role="tablist">
                        {TABS.map(t => (
                            <button
                                key={t.id}
                                role="tab"
                                aria-selected={tab === t.id}
                                className={`ml-tab${tab === t.id ? ' is-active' : ''}`}
                                onClick={() => setTab(t.id)}
                            >
                                {t.label}
                                <span className="ml-tab-count">{counts[t.id] || 0}</span>
                            </button>
                        ))}
                    </nav>

                    {/* Listing cards */}
                    <div className="ml-list">
                        {filteredJobs.length === 0 ? (
                            <div className="ml-empty">
                                <div className="ml-empty-icon"><Briefcase size={26} /></div>
                                <h3 className="ml-empty-title">
                                    {{
                                        ACTIVE:    'No active listings',
                                        SCHEDULED: 'Nothing scheduled',
                                        COMPLETED: 'No completed jobs yet',
                                        DRAFT:     'No drafts',
                                    }[tab]}
                                </h3>
                                <p className="lede" style={{ maxWidth: '380px', margin: '0 auto 20px' }}>
                                    {{
                                        ACTIVE:    'Post a job to start receiving candidates.',
                                        SCHEDULED: 'Once you accept a candidate, the job moves here.',
                                        COMPLETED: 'Wrapped jobs and ratings show up here.',
                                        DRAFT:     'Save a job mid-flow and you\'ll find it here.',
                                    }[tab]}
                                </p>
                                {tab === 'ACTIVE' && (
                                    <Link to="/create-job" className="ix-btn ix-btn-primary">
                                        <Plus size={14} /> Post a job
                                    </Link>
                                )}
                            </div>
                        ) : filteredJobs.map(job => (
                            <ListingCard
                                key={job.id}
                                job={job}
                                applications={appsByJob[job.id] || []}
                                statuses={statuses}
                                onSetStatus={handleSetStatus}
                                onOpen={() => {
                                    const apps = appsByJob[job.id] || [];
                                    setPanelJobId(job.id);
                                    setPanelSelected(apps[0] || null);
                                }}
                                onOpenCandidate={app => {
                                    setPanelJobId(job.id);
                                    setPanelSelected(app);
                                }}
                                onMarkDone={() => setMarkDoneJobId(job.id)}
                                onCancel={() => setCancelJobId(job.id)}
                            />
                        ))}
                    </div>
                </div>
            </main>

            {/* Candidate panel */}
            {panelJob && (
                <CandidatePanel
                    job={panelJob}
                    applications={appsByJob[panelJob.id] || []}
                    selected={panelSelected}
                    onSelect={setPanelSelected}
                    onClose={() => { setPanelJobId(null); setPanelSelected(null); }}
                    statuses={statuses}
                    onSetStatus={handleSetStatus}
                    onViewProfile={app => setProfileApp(app)}
                />
            )}

            {/* Mark Done modal */}
            {markDoneJob && (
                <MarkDoneModal
                    job={markDoneJob}
                    acceptedApps={(appsByJob[markDoneJob.id] || []).filter(a =>
                        (statuses[a.id] || a.status) === 'ACCEPTED'
                    )}
                    onClose={() => setMarkDoneJobId(null)}
                    onConfirm={handleMarkDone}
                />
            )}

            {/* Cancel modal */}
            {cancelJob && (
                <CancelModal
                    job={cancelJob}
                    onClose={() => setCancelJobId(null)}
                    onConfirm={handleCancel}
                />
            )}

            {/* Toast */}
            {toast && <div className="ml-toast">{toast}</div>}
        </div>
        </>
    );
};

export default MyListings;
